import { groupBy, isNil, isPlainObject, isString, mapValues, partition, sortBy } from "lodash";

export type QueryParamValue = string | QueryParamValue[] | QueryParams | undefined;

export type QueryParams = { [key: string]: QueryParamValue };

type TypeGuard<T> = (value: unknown) => value is T;

const firstValue = (value: QueryParamValue): QueryParamValue =>
  Array.isArray(value) ? value[0] : value;

const allValues = (value: QueryParamValue): QueryParamValue[] | undefined =>
  isNil(value) ? undefined : Array.isArray(value) ? value : [value];

export function singleValue(value: QueryParamValue): string | undefined;
export function singleValue<T>(value: QueryParamValue, typeGuard: TypeGuard<T>): T | undefined;
export function singleValue(
  value: QueryParamValue,
  typeGuard: TypeGuard<unknown> = isString,
): unknown {
  const first = firstValue(value);
  return typeGuard(first) ? first : undefined;
}

export function multiValue(value: QueryParamValue): string[] | undefined;
export function multiValue<T>(value: QueryParamValue, typeGuard: TypeGuard<T>): T[] | undefined;
export function multiValue(
  value: QueryParamValue,
  typeGuard: TypeGuard<unknown> = isString,
): unknown[] | undefined {
  return allValues(value)?.filter((v) => typeGuard(v));
}

const encode = (k: string): string => new URLSearchParams({ "": k }).toString().slice(1);

const encodePath = (path: (string | number)[]): string =>
  path.reduce<string>((acc, k) => {
    if (typeof k === "number") {
      return acc + `[${k}]`;
    }
    if (acc) {
      return acc + `.${encode(k)}`;
    }
    return encode(k);
  }, "");

const toPairs = (path: (string | number)[], value: unknown): string[] => {
  if (isNil(value)) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((v, i) => toPairs([...path, i], v));
  }
  if (isPlainObject(value)) {
    return Object.entries(value).flatMap(([k, v]) => toPairs([...path, k], v));
  }
  return [`${encodePath(path)}=${encode(String(value))}`];
};

export function toQueryString(params: { [key: string]: unknown } | undefined): string {
  const s = Object.entries(params ?? {})
    .flatMap(([key, value]) => toPairs([key], value))
    .join("&");
  return s ? `?${s}` : "";
}

const PATH_TOKEN = /\[\d*\]|[^.[\]]+/g;

type Entry = { path: string[]; value: string };

const isIndexed = ({ path }: Entry): boolean => path[0].startsWith("[");

const isKeyed = ({ path }: Entry): boolean => path.length > 0 && !path[0].startsWith("[");

const tail = ({ path, value }: Entry): Entry => ({ path: path.slice(1), value });

const buildValue = (entries: Entry[]): QueryParamValue => {
  const [leaves, nested] = partition(entries, ({ path }) => path.length === 0);
  if (nested.length === 0) {
    return leaves.length === 1 ? leaves[0].value : leaves.map(({ value }) => value);
  }
  const [indexed, keyed] = partition(nested, isIndexed);
  return indexed.length > 0 ? buildArray(indexed) : buildObject(keyed);
};

const buildArray = (entries: Entry[]): QueryParamValue[] => {
  const [appended, indexed] = partition(entries, ({ path }) => path[0] === "[]");
  const byIndex = sortBy(Object.entries(groupBy(indexed, ({ path }) => path[0])), ([token]) =>
    Number(token.slice(1, -1)),
  );
  return [
    ...byIndex.map(([, group]) => buildValue(group.map(tail))),
    ...appended.map((entry) => buildValue([tail(entry)])),
  ];
};

const buildObject = (entries: Entry[]): QueryParams =>
  mapValues(
    groupBy(entries, ({ path }) => path[0]),
    (group) => buildValue(group.map(tail)),
  );

export function fromQueryString(s: string | undefined): QueryParams {
  const entries = [...new URLSearchParams(s ?? "")].map(([key, value]) => ({
    path: key.match(PATH_TOKEN) ?? [],
    value,
  }));
  return buildObject(entries.filter(isKeyed));
}

export function stripQueryString(s: string) {
  let end = s.indexOf("?");
  if (end === -1) {
    end = s.length;
  }
  return s.substring(0, end);
}
